import { KeyboardEvent, useEffect, useState } from "react";
import { Button, Form, Input, Loader, Message, Modal, Table } from "rsuite";
import { Driver } from "../my-type";
import { authUserData } from "../service";
import { searchDrivers } from "../service/DriverService";

interface DriverSearchModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (driver: Driver) => void;
  teamCode?: string;
  title?: string;
  initialKeyword?: string;
}

const { Column, HeaderCell, Cell } = Table;

export default function DriverSearchModal({
  open,
  onClose,
  onSelect,
  teamCode,
  title = "搜尋司機",
  initialKeyword = "",
}: DriverSearchModalProps) {
  const authUser = authUserData();
  const [keyword, setKeyword] = useState(initialKeyword);
  const [results, setResults] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  useEffect(() => {
    if (open) {
      setKeyword(initialKeyword);
      setResults([]);
      setErrorMessage("");
      setInfoMessage("");
    }
  }, [open, initialKeyword]);

  const resolvedTeamCode = teamCode || authUser?.team?.code;

  const handleSearch = async () => {
    const trimmedKeyword = keyword.trim();
    setErrorMessage("");
    setInfoMessage("");

    if (!resolvedTeamCode) {
      setErrorMessage("無法取得團隊代碼，請重新登入或聯絡管理員。");
      return;
    }

    if (!trimmedKeyword) {
      setErrorMessage("請輸入司機姓名或電話。");
      return;
    }

    setLoading(true);
    const data = await searchDrivers(resolvedTeamCode, trimmedKeyword);
    setLoading(false);

    if (!data) {
      setErrorMessage("搜尋失敗，請稍後再試。");
      setResults([]);
      return;
    }

    setResults(data);
    if (data.length === 0) {
      setInfoMessage("沒有找到符合條件的司機。");
    } else {
      setInfoMessage(`找到 ${data.length} 位司機。`);
    }
  };

  const handleClear = () => {
    setKeyword("");
    setResults([]);
    setErrorMessage("");
    setInfoMessage("");
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleSelect = (driver: Driver) => {
    onSelect(driver);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} size="md">
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form fluid>
          <Form.Group>
            <Form.ControlLabel>關鍵字</Form.ControlLabel>
            <Input
              placeholder="輸入司機姓名或電話"
              value={keyword}
              onChange={(value) => setKeyword(value)}
              onKeyPress={handleKeyPress}
            />
          </Form.Group>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <Button appearance="primary" onClick={handleSearch} disabled={loading}>
              搜尋
            </Button>
            <Button appearance="subtle" onClick={handleClear} disabled={loading}>
              清除
            </Button>
          </div>
        </Form>

        {loading && <Loader center content="搜尋中..." />}
        {!loading && errorMessage && (
          <Message type="error" showIcon>
            {errorMessage}
          </Message>
        )}
        {!loading && !errorMessage && infoMessage && (
          <Message type="info" showIcon>
            {infoMessage}
          </Message>
        )}

        {results.length > 0 && (
          <Table
            data={results}
            autoHeight
            rowHeight={48}
            style={{ marginTop: 12 }}
          >
            <Column width={140} align="center" fixed>
              <HeaderCell>姓名</HeaderCell>
              <Cell dataKey="driverName" />
            </Column>
            <Column width={150} align="center">
              <HeaderCell>電話</HeaderCell>
              <Cell dataKey="phoneNumber" />
            </Column>
            <Column width={150} align="center">
              <HeaderCell>車號</HeaderCell>
              <Cell dataKey="licencePlateNumber" />
            </Column>
            <Column width={100} align="center">
              <HeaderCell>操作</HeaderCell>
              <Cell>
                {(rowData: Driver) => (
                  <Button appearance="link" onClick={() => handleSelect(rowData)}>
                    選擇
                  </Button>
                )}
              </Cell>
            </Column>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} appearance="subtle">
          關閉
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
